import { QRCodeSVG } from "qrcode.react";
import { forwardRef } from "react";
import { API_BASE } from "../lib/api";
import { buildTicketModel } from "../lib/ticketFormat";

// One printed line with a receipt text style (normal | tall | wide | big, bold).
function Line({ st, center, testId, className = "", children }) {
  return (
    <div className={`pt-s-${st.size} ${st.bold ? "pt-b" : ""} ${center ? "pt-c" : ""} ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

// Compact thermal receipt (58mm/80mm). Layout mirrors lib/escpos.js (RawBT).
const PrintableTicket = forwardRef(function PrintableTicket({ ticket, settings, isReprint }, ref) {
  if (!ticket) return null;

  const paper = settings?.receipt_paper_size === "58mm" ? "58mm" : "80mm";
  const is58 = paper === "58mm";
  const m = buildTicketModel(ticket, settings || {});
  const st = m.styles;
  const showCopy = isReprint && (settings?.receipt_show_copy_mark ?? true);
  const logoSrc = m.showLogo && settings?.receipt_logo_path
    ? `${API_BASE}/settings/logo?consortium_id=${encodeURIComponent(settings?.consortium_id || "")}&v=${encodeURIComponent(settings.receipt_logo_path)}`
    : null;
  const publicUrl = ticket.ticket_number && typeof window !== "undefined"
    ? `${window.location.origin}/t/${encodeURIComponent(ticket.ticket_number)}${ticket.share_token ? `?v=${encodeURIComponent(ticket.share_token)}` : ""}`
    : m.ticketNo;
  const isWide = (k) => st[k].size === "wide" || st[k].size === "big";
  const narrowPlays = isWide("plays");
  const narrowHeader = isWide("header");
  const totalLine = `Total: ${m.total}`;
  const wideTotal = st.total.size === "wide" || st.total.size === "big";
  const totalSt = wideTotal && totalLine.length > (is58 ? 16 : 24) ? { ...st.total, size: "tall" } : st.total;
  const qrPx = { small: is58 ? 64 : 80, medium: is58 ? 84 : 100, large: is58 ? 110 : 130 }[m.qrSize];
  const logoMm = { small: 9, medium: 14, large: 20 }[m.logoSize];

  return (
    <div ref={ref} className="printable-ticket" data-testid="printable-ticket">
      <style>{`
        @media print {
          @page { size: ${paper} auto; margin: 2mm; }
          html, body { background: white !important; }
          body:has(.print-root) > *:not(.print-root) { display: none !important; }
          .print-root {
            display: block !important; position: static !important; opacity: 1 !important;
            z-index: auto !important; pointer-events: auto !important; inset: auto !important;
          }
        }
        .printable-ticket {
          width: ${is58 ? "52mm" : "72mm"};
          padding: 3mm 2mm;
          font-family: 'JetBrains Mono', 'Courier New', monospace;
          font-size: ${is58 ? "9.5px" : "12.5px"};
          line-height: 1.25;
          color: #000; background: #fff; font-weight: 500;
          overflow: hidden;
        }
        .pt-c { text-align: center; }
        .pt-b { font-weight: 800; }
        .pt-s-tall { transform: scaleY(1.8); transform-origin: top; margin-bottom: 1em; }
        .pt-s-wide { width: 50%; transform: scaleX(2); transform-origin: top left; }
        .pt-s-big { width: 50%; transform: scale(2); transform-origin: top left; margin-bottom: 1.25em; }
        .pt-gap { height: 0.7em; }
        .pt-grid { display: grid; grid-template-columns: 3.5ch minmax(0, 1fr) 7ch auto; white-space: nowrap; }
        .pt-grid-n { grid-template-columns: 3.5ch minmax(0, 1fr) 3.5ch auto; }
        .pt-grid > :last-child { text-align: right; }
        .pt-hr { border: none; border-top: 1.5px dashed #000; margin: 2px 0 4px; }
        .pt-logo { display: block; max-width: 70%; max-height: ${logoMm}mm; margin: 0 auto 3px; object-fit: contain; }
        .pt-qr { display: flex; justify-content: center; margin: 4px 0 6px; }
      `}</style>

      {logoSrc && <img src={logoSrc} alt="" className="pt-logo" crossOrigin="anonymous" data-testid="ticket-logo" />}
      <Line st={st.business} center testId="ticket-business-name">{m.bizName}</Line>
      {m.slogan && <Line st={st.slogan} center testId="ticket-slogan">{m.slogan}</Line>}
      {showCopy && <div className="pt-c pt-b">** COPIA **</div>}

      <Line st={st.meta} testId="ticket-date">Fecha: {m.fecha}</Line>
      {m.serial && <Line st={st.meta} testId="ticket-serial">Serial:{m.serial}</Line>}
      <Line st={st.meta} testId="ticket-number">Ticket No: {m.ticketNo}</Line>
      {m.codeNo && <Line st={st.meta} testId="ticket-code">Codigo No: {m.codeNo}</Line>}
      {m.title && <><div className="pt-gap" /><Line st={st.title} center testId="ticket-title">{m.title}</Line></>}
      <div className="pt-gap" />

      {m.legend.map((l) => (
        <Line key={l.abbr} st={st.legend} testId={`ticket-legend-${l.abbr}`}>{l.abbr} -&gt; {l.name}</Line>
      ))}
      <Line st={st.header} className={`pt-grid ${narrowHeader ? "pt-grid-n" : ""}`} testId="ticket-table-header">
        <span style={{ gridColumn: "1 / 3" }}>{narrowHeader ? "Pr Num" : "Prod. Numero"}</span><span>{narrowHeader ? "Lot" : "Loter."}</span><span>{narrowHeader ? "Val" : "Valor"}</span>
      </Line>
      <hr className="pt-hr" />
      {m.rows.map((r, i) => (
        <Line key={r.key || i} st={st.plays} className={`pt-grid ${narrowPlays ? "pt-grid-n" : ""}`} testId={`ticket-row-${i}`}>
          <span>{r.prod}</span><span>{r.num}</span><span>{r.lot}</span><span>{narrowPlays ? r.amt.replace("RD$ ", "") : r.amt}</span>
        </Line>
      ))}
      <hr className="pt-hr" />
      <Line st={totalSt} center testId="ticket-total">{totalLine}</Line>

      {m.bottomLines.map((l) => <Line key={l} st={st.footer} center>{l}</Line>)}
      {m.showQr && (
        <div className="pt-qr" data-testid="ticket-qr">
          <QRCodeSVG value={publicUrl} size={qrPx} level="M" includeMargin={false} />
        </div>
      )}
    </div>
  );
});

export default PrintableTicket;

/**
 * Helper: print a ticket by rendering it inline as a .print-root child of
 * <body>. The @media print rules hide everything else. Works on iOS Safari,
 * Android, and desktop. Pass `isReprint=true` for the reprint / duplicate flow.
 */
export async function printTicket(ticket, options = {}) {
  const { isReprint = false, settings: providedSettings } = options;

  // Ensure we have current settings (paper size + business name + logo + footer)
  let effectiveSettings = providedSettings;
  if (!effectiveSettings) {
    try {
      const api = (await import("../lib/api")).default;
      const { data } = await api.get("/settings");
      effectiveSettings = data;
    } catch { effectiveSettings = {}; }
  }

  // Bluetooth via RawBT (Android) path — hijack the flow when the cashier
  // has selected that printer type on this terminal.
  const printerType = (() => {
    try { return localStorage.getItem("pos_printer_type") || "browser"; } catch { return "browser"; }
  })();
  if (printerType === "bluetooth_rawbt") {
    try {
      const { buildTicketBase64, sendToRawBT, logoUrlToRaster } = await import("../lib/escpos");
      const publicUrl = ticket.ticket_number
        ? `${window.location.origin}/t/${encodeURIComponent(ticket.ticket_number)}${ticket.share_token ? `?v=${encodeURIComponent(ticket.share_token)}` : ""}`
        : null;

      // Rasterize the business logo so Bluetooth tickets show it too.
      // Width is tied to paper size (240 dots on 80mm, 160 on 58mm) and
      // height is capped so a tall logo doesn't eat extra paper.
      const paperSize = effectiveSettings.receipt_paper_size === "58mm" ? "58mm" : "80mm";
      let logoRaster = null;
      if (effectiveSettings.receipt_logo_path && (effectiveSettings.receipt_show_logo ?? true)) {
        const logoUrl = `${API_BASE}/settings/logo?consortium_id=${encodeURIComponent(effectiveSettings.consortium_id || "")}&v=${encodeURIComponent(effectiveSettings.receipt_logo_path)}`;
        const maxW = paperSize === "58mm" ? 160 : 240;
        try {
          const maxH = { small: 60, medium: 100, large: 140 }[effectiveSettings.receipt_logo_size || "medium"];
          logoRaster = await logoUrlToRaster(logoUrl, maxW, maxH);
        } catch (e) {
          console.warn("Logo rasterize failed, printing without logo", e);
        }
      }

      const b64 = buildTicketBase64({
        ticket,
        settings: effectiveSettings,
        paperSize,
        isReprint,
        publicUrl,
        logoRaster,
      });
      sendToRawBT(b64);
      return;
    } catch (e) {
      console.warn("RawBT print failed, falling back to browser print", e);
      // fallthrough to browser print
    }
  }

  // Clean up any stale print-root from previous attempts.
  document.querySelectorAll(".print-root").forEach((n) => n.remove());

  const holder = document.createElement("div");
  holder.className = "print-root";
  holder.style.position = "fixed";
  holder.style.inset = "0";
  holder.style.zIndex = "-1";
  holder.style.pointerEvents = "none";
  holder.style.opacity = "0";
  document.body.appendChild(holder);

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    window.removeEventListener("afterprint", cleanup);
    try { rootRef && rootRef.unmount(); } catch {}
    try { holder.remove(); } catch {}
  };
  let rootRef = null;

  const { createRoot } = await import("react-dom/client");
  rootRef = createRoot(holder);
  rootRef.render(<PrintableTicket ticket={ticket} settings={effectiveSettings} isReprint={isReprint} />);

  setTimeout(async () => {
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      const img = holder.querySelector(".pt-logo");
      if (img && !img.complete) {
        await new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
          setTimeout(res, 2000);
        });
      }
    } catch {}
    window.addEventListener("afterprint", cleanup);
    setTimeout(cleanup, 8000);
    window.print();
  }, 250);
}
